import { apiClient } from './axios';

import type { MisclassifiedCall } from './misclassified-calls.api';

export type JudgeTuningStatus =
  | 'DRAFT'
  | 'QUEUED'
  | 'RUNNING'
  | 'SUCCEEDED'
  | 'PARTIAL'
  | 'FAILED'
  | 'CANCELLED';

/** Sample-weighted aggregate across all target configs that ran. */
export interface JudgeTuningResultSummary {
  baselinePassRate?: number;
  bestPassRate?: number;
  totalSamples?: number;
  configCount?: number;
  applied?: boolean;
  appliedAt?: string;
}

/** Live progress written while a run is in flight. */
export type JudgeTuningProgressStage =
  | 'collecting'
  | 'baseline'
  | 'optimizing'
  | 'evaluating'
  | 'finalizing'
  | 'done'
  | 'failed';

export interface JudgeTuningProgress {
  stage: JudgeTuningProgressStage;
  configId?: string;
  configName?: string;
  iteration?: number;
  iterations?: number;
  current?: number;
  total?: number;
  message: string;
  updatedAt: string;
}

export type JudgeTuningConfigStatus = 'SUCCEEDED' | 'PARTIAL' | 'FAILED' | 'SKIPPED';

/** Per-config run outcome (judge_tuning_runs.config_results, keyed by config id). */
export interface JudgeTuningConfigResult {
  configName: string;
  status: JudgeTuningConfigStatus;
  error?: string;
  basePrompt?: string;
  candidatePrompt?: string;
  baselinePassRate?: number;
  bestPassRate?: number;
  bestIteration?: number;
  totalSamples?: number;
  applied?: boolean;
  appliedAt?: string;
  /** True when at least one sample used corrections derived from a note. */
  usedDerivedCorrections?: boolean;
  /** Call ids where a note existed but could not be mapped to field corrections. */
  unmappedNoteCallIds?: string[];
}

export interface JudgeTuningSampleResult {
  call_id: string;
  predicted_labels: Record<string, boolean>;
  predicted_data: Record<string, unknown>;
  expected_labels: Record<string, boolean>;
  expected_data: Record<string, unknown>;
  match: boolean;
  mismatchedKeys: string[];
}

/** An edit the optimizer proposed against the prompt. */
export interface JudgeTuningPromptEdit {
  op: 'replace' | 'insert_after' | 'insert_before' | 'delete';
  find: string;
  replace?: string;
}

/**
 * Iteration results can be a flat array (legacy/baseline) or a structured
 * object with edits when the optimizer used the edit-based approach.
 */
export type JudgeTuningIterationResults =
  | JudgeTuningSampleResult[]
  | {
      samples: JudgeTuningSampleResult[];
      edits?: JudgeTuningPromptEdit[];
      append?: string;
    };

export interface JudgeTuningIteration {
  id: string;
  judge_tuning_run_id: string;
  target_config_id: string;
  iteration: number;
  prompt: string;
  rationale: string | null;
  passed_count: number;
  total_count: number;
  results: JudgeTuningIterationResults;
  created_at: string;
}

export interface JudgeTuningRun {
  id: string;
  name: string;
  target_type: string;
  target_config_ids: string[];
  target_all_used_configs: boolean;
  auto_update: boolean;
  apply_strategy: 'pass_rate' | 'best_candidate';
  apply_min_pass_rate: number;
  optimizer_model: string;
  max_iterations: number;
  status: JudgeTuningStatus;
  sample_tags: string[];
  sample_tag_match: 'any' | 'all';
  included_call_ids: string[];
  excluded_call_ids: string[];
  config_results: Record<string, JudgeTuningConfigResult>;
  result_summary: JudgeTuningResultSummary;
  progress: JudgeTuningProgress | null;
  error: string | null;
  created_at: string;
  updated_at: string;
}

export interface JudgeTuningRunListItem extends JudgeTuningRun {
  _count: { iterations: number };
}

export interface JudgeTuningRunDetail extends JudgeTuningRun {
  iterations: JudgeTuningIteration[];
}

export interface CreateJudgeTuningRunInput {
  name: string;
  target_type?: string;
  target_config_ids?: string[];
  target_all_used_configs?: boolean;
  auto_update?: boolean;
  apply_strategy?: 'pass_rate' | 'best_candidate';
  apply_min_pass_rate?: number;
  optimizer_model?: string;
  max_iterations?: number;
  sample_tags?: string[];
  sample_tag_match?: 'any' | 'all';
  included_call_ids?: string[];
  excluded_call_ids?: string[];
}

export type UpdateJudgeTuningRunInput = Partial<CreateJudgeTuningRunInput>;

export type { FeedbackTargetType } from './misclassified-calls.api';

export interface SubmitCorrectionInput {
  call_id: string;
  target_type: 'classifier' | 'quality' | 'review';
  target_config_id: string;
  expected_labels: Record<string, boolean>;
  expected_data: Record<string, unknown>;
  reason?: string;
  include_other_corrections?: boolean;
  tune: boolean;
}

export interface SubmitCorrectionResult {
  registryRow: MisclassifiedCall;
  run?: JudgeTuningRun;
}

export const judgeTuningApi = {
  list: async (): Promise<JudgeTuningRunListItem[]> => {
    const response = await apiClient.get<JudgeTuningRunListItem[]>(
      '/agent-improvement/judge-tuning',
    );
    return response.data;
  },
  getById: async (id: string): Promise<JudgeTuningRunDetail> => {
    const response = await apiClient.get<JudgeTuningRunDetail>(
      `/agent-improvement/judge-tuning/${id}`,
    );
    return response.data;
  },
  samples: async (id: string): Promise<MisclassifiedCall[]> => {
    const response = await apiClient.get<MisclassifiedCall[]>(
      `/agent-improvement/judge-tuning/${id}/samples`,
    );
    return response.data;
  },
  create: async (data: CreateJudgeTuningRunInput): Promise<JudgeTuningRunDetail> => {
    const response = await apiClient.post<JudgeTuningRunDetail>(
      '/agent-improvement/judge-tuning',
      data,
    );
    return response.data;
  },
  update: async (id: string, data: UpdateJudgeTuningRunInput): Promise<JudgeTuningRunDetail> => {
    const response = await apiClient.put<JudgeTuningRunDetail>(
      `/agent-improvement/judge-tuning/${id}`,
      data,
    );
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/agent-improvement/judge-tuning/${id}`);
  },
  run: async (id: string): Promise<JudgeTuningRunDetail> => {
    const response = await apiClient.post<JudgeTuningRunDetail>(
      `/agent-improvement/judge-tuning/${id}/run`,
    );
    return response.data;
  },
  /** Without configId applies every unapplied candidate; with it, that config only. */
  apply: async (id: string, configId?: string): Promise<JudgeTuningRunDetail> => {
    const response = await apiClient.post<JudgeTuningRunDetail>(
      `/agent-improvement/judge-tuning/${id}/apply`,
      configId ? { config_id: configId } : {},
    );
    return response.data;
  },
  /** Submit a correction from the call page and optionally start a tuning run. */
  submitCorrection: async (data: SubmitCorrectionInput): Promise<SubmitCorrectionResult> => {
    const response = await apiClient.post<SubmitCorrectionResult>(
      '/agent-improvement/judge-tuning/corrections',
      data,
    );
    return response.data;
  },
  /** Get judge-tuning runs that include a specific call. */
  getRunsForCall: async (callId: string): Promise<JudgeTuningRun[]> => {
    const response = await apiClient.get<JudgeTuningRun[]>(
      `/agent-improvement/judge-tuning/by-call/${callId}`,
    );
    return response.data;
  },
};
